/** Flug eines Einsatzes (Antwort von GET /fluege?einsatz_id=…) */
export interface EinsatzFlug {
  id: number
  flug_nr?: string | null
  drohne_id?: number | null
  pilot_id?: number | null
  startzeit?: string | null
  endzeit?: string | null
  status?: string | null
}

const fmt = (s?: string | null) => (s ? new Date(s).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—')
function dauer(f: EinsatzFlug): string {
  if (!f.startzeit || !f.endzeit) return '—'
  const ms = new Date(f.endzeit).getTime() - new Date(f.startzeit).getTime()
  if (ms <= 0) return '—'
  const min = Math.round(ms / 60000)
  return min >= 60 ? `${Math.floor(min / 60)} h ${min % 60} min` : `${min} min`
}

/**
 * Liste der Flüge eines Einsatzes. Mit `onOeffnen` ist jede Zeile anklickbar (Admin: Flugansicht),
 * ohne bleibt es eine reine Anzeige (Führung).
 */
export function EinsatzFluege({ fluege, laedt, fehler, drohneLabel, pilotLabel, onOeffnen }: {
  fluege: EinsatzFlug[]
  laedt?: boolean
  fehler?: string | null
  drohneLabel: (id: number | null | undefined) => string
  pilotLabel: (id: number | null | undefined) => string
  onOeffnen?: (f: EinsatzFlug) => void
}) {
  if (laedt && fluege.length === 0) return <p className="text-sm text-dark-text-muted">Lade Flüge…</p>
  if (fehler) return <p className="text-sm text-red-300">{fehler}</p>
  if (fluege.length === 0) return <p className="text-sm text-dark-text-subtle">Zu diesem Einsatz gibt es noch keine Flüge.</p>
  const status = (f: EinsatzFlug) => f.endzeit
    ? <span className="rounded-full bg-green-600/20 px-2 py-0.5 text-xs font-semibold text-green-300">Abgeschlossen</span>
    : <span className="rounded-full bg-primary-600/20 px-2 py-0.5 text-xs font-semibold text-primary-300">Laufend</span>
  const klick = (f: EinsatzFlug) => onOeffnen ? {
    role: 'button' as const, tabIndex: 0, onClick: () => onOeffnen(f),
    onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOeffnen(f) } },
  } : {}
  const zeiger = onOeffnen ? 'cursor-pointer hover:bg-dark-bg-tertiary/60' : ''
  return (
    <div data-testid="einsatz-fluege">
      <ul className="space-y-2 md:hidden">
        {fluege.map(f => (
          <li key={f.id} data-flug-id={f.id} {...klick(f)} className={`rounded-xl border border-dark-border bg-dark-bg-tertiary/30 p-3 [@media(pointer:coarse)]:min-h-[44px] ${zeiger}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-sm font-semibold">{f.flug_nr || `#${f.id}`}</span>{status(f)}
            </div>
            <dl className="mt-2 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 text-sm">
              <dt className="text-dark-text-subtle">Drohne</dt><dd className="min-w-0 break-words">{drohneLabel(f.drohne_id)}</dd>
              <dt className="text-dark-text-subtle">Pilot</dt><dd className="min-w-0 break-words">{pilotLabel(f.pilot_id)}</dd>
              <dt className="text-dark-text-subtle">Start</dt><dd>{fmt(f.startzeit)}</dd>
              <dt className="text-dark-text-subtle">Dauer</dt><dd>{dauer(f)}</dd>
            </dl>
          </li>
        ))}
      </ul>
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-dark-text-subtle">
              <th className="px-3 py-2">Flugnummer</th><th className="px-3 py-2">Drohne</th><th className="px-3 py-2">Pilot</th>
              <th className="px-3 py-2">Start</th><th className="px-3 py-2">Dauer</th><th className="px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {fluege.map(f => (
              <tr key={f.id} data-flug-id={f.id} {...klick(f)} className={`border-t border-dark-border [@media(pointer:coarse)]:h-11 ${zeiger}`}>
                <td className="px-3 py-2 font-mono">{f.flug_nr || `#${f.id}`}</td>
                <td className="px-3 py-2">{drohneLabel(f.drohne_id)}</td>
                <td className="px-3 py-2">{pilotLabel(f.pilot_id)}</td>
                <td className="px-3 py-2 whitespace-nowrap">{fmt(f.startzeit)}</td>
                <td className="px-3 py-2 whitespace-nowrap">{dauer(f)}</td>
                <td className="px-3 py-2">{status(f)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
